<template>
  <base-modal
    :ref="modalKey"
    :show="open"
    :title="modalTitle"
    @on-confirm="saveItem"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    max-width="80%"
  >
    <v-row justify="center" align="center" v-if="loading">
      <div class="text-center">
        <div>
          <v-progress-circular
            class="my-4"
            :size="50"
            width="5"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
        <p class="display-1">{{ $t("Loading") }}...</p>
      </div>
    </v-row>
    <DxDataGrid
      v-if="!loading"
      id="gridContainer"
      :data-source="dataSource"
      :show-borders="true"
      key-expr="Id"
      @row-inserted="onRowInserted"
      @row-updated="onRowUpdated"
      @row-removed="onRowDeleted"
    >
      <dx-search-panel :visible="true" :highlight-case-sensitive="true" />
      <dx-filter-panel :visible="true" />
      <dx-filter-row :visible="true" />
      <DxPaging :enabled="true" />

      <DxEditing
        :allow-updating="true"
        :allow-deleting="true"
        :allow-adding="true"
        mode="row"
      />

      <DxColumn
        v-for="(column, index) in internalTable.columns"
        :caption="getColumnLabel(column)"
        :data-field="column.name"
        :key="index"
      >
        <DxRequiredRule v-if="column.isRequired" />
        <DxLookup
          v-if="column.relatedTableId"
          :data-source="relatedTableListElements[column.relatedTableId]"
          display-expr="text"
          value-expr="id"
        />
      </DxColumn>
    </DxDataGrid>
  </base-modal>
</template>
<script>
import InternalTableService from "@/services/InternalTableService";
import InternalTable from "@/ecm/models/internalTable/InternalTable";
import InternalTableRow from "@/ecm/models/internalTable/InternalTableRow";
import InternalTableRowItem from "@/ecm/models/internalTable/InternalTableRowItem";
import DxButton from "devextreme-vue/button";
import Utils from "@/ecm/common/Utils";
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxRequiredRule,
  DxPaging,
  DxLookup,
  DxFilterPanel,
  DxSearchPanel,
  DxFilterRow,
} from "devextreme-vue/data-grid";

export default {
  name: "InternalTableDataModal",
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxRequiredRule,
    DxPaging,
    DxButton,
    DxLookup,
    DxFilterPanel,
    DxSearchPanel,
    DxFilterRow,
  },
  data() {
    return {
      modalKey: "internalTableDataModal",
      dataSource: [],
      open: false,
      relatedTableListElements: [],
      internalTable: new InternalTable(),
      rowsToSave: [],
      loading: false,
      isNew: true,
    };
  },
  computed: {
    modalTitle() {
      return (
        this.$t("TableData") +
        (this.internalTable.name ? " - " + this.internalTable.name : "")
      );
    },
    modalInstance() {
      return this.$refs[this.modalKey];
    },
  },
  methods: {
    getLinkedTableData(relatedTableId) {
      InternalTableService.getLinkedTableData(parseInt(relatedTableId)).then(
        (data) => {
          this.relatedTableListElements[relatedTableId] = data;
        }
      );
    },
    getColumnLabel(column) {
      return column.isRequired ? column.name + "*" : column.name;
    },
    onRowInserted(row) {
      var newRow = new InternalTableRow();
      newRow.id = row.key;
      newRow.items = Object.keys(row.data)
        .filter((x) => x != "Id")
        .map((x) => {
          var rowItem = new InternalTableRowItem();
          rowItem.column.name = x;
          rowItem.value = row.data[x];
          return rowItem;
        });
      this.rowsToSave.push(newRow);
    },
    onRowUpdated(row) {
      var updRow = this.rowsToSave.find((x) => x.id == row.key);
      if (!updRow) {
        updRow = new InternalTableRow();
        updRow.id = row.key;
      }
      updRow.items = Object.keys(row.data)
        .filter((x) => x != "Id")
        .map((x) => {
          var rowItem = new InternalTableRowItem();
          rowItem.column.name = x;
          rowItem.value = row.data[x];
          return rowItem;
        });
      this.rowsToSave.push(updRow);
    },
    onRowDeleted(row) {
      var delRow = this.rowsToSave.find((x) => x.id == row.key);
      if (!delRow) {
        delRow = new InternalTableRow();
        delRow.id = row.key;
        this.rowsToSave.push(delRow);
      } else {
        this.rowsToSave = this.rowsToSave.filter((x) => x.id != delRow.id);
      }
    },
    validateRequiredColumns(row) {
      return true;
    },
    saveItem() {
      if (this.validateRequiredColumns()) {
        this.modalInstance.progress(true);
        InternalTableService.saveData(
          this.internalTable.id,
          this.rowsToSave.map((x) => x.toDto())
        )
          .then((result) => {
            if (result) {
              if (this.isNew) {
                Utils.toast.success(
                  this.$t("InternalTable") + " " + this.$t("CreatedSuccessfullyFem")
                );
              } else {
                Utils.toast.success(
                  this.$t("InternalTable") + " " + this.$t("UpdatedSuccessfullyFem")
                );
              }
              this.hide();
              this.$emit("on-item-saved");
            }
          })
          .catch((error) => {
            // error
          })
          .then(() => {
            //always
            this.modalInstance.progress(false);
          });
      }
    },
    async edit(dto) {
      this.internalTable = new InternalTable(dto);
      this.isNew = false;
      this.dataSource = [];
      this.rowsToSave = [];
      var self = this;
      await Promise.all(
        this.internalTable.columns
          .filter((c) => c.relatedTableId)
          .map(async (column) => {
            await self.getLinkedTableData(column.relatedTableId);
          })
      );
      this.loading = true;
      InternalTableService.getData(dto.Id).then((data) => {
        this.dataSource = data.map((row) => {
          var gridRow = {
            Id: row.Id,
          };
          row.Items.forEach((item) => {
            gridRow[item.Column.Name] = item.Value;
          });
          return gridRow;
        });

        this.loading = false;
      });
      this.show();
    },
    show() {
      this.open = true;
    },
    hide() {
      this.open = false;
    },
    remove() {
      this.internalTable.columns.splice();
    },
  },
};
</script>
